import React, { useRef } from 'react';
import styled, { keyframes } from 'styled-components';
import { MediaQuery } from 'core/config';
import { useIntersectionObserver } from 'view/hooks';
import patentBottomImage from '../../../../assets/images/home-patent/patent-bottom-image.png';
import patentBottomImageTablet from '../../../../assets/images/home-patent/patent-bottom-image-tablet.png';
import patentBottomImageMobile from '../../../../assets/images/home-patent/patent-bottom-image-mobile.png';

const fadeIn = keyframes`
  from {
    opacity: 0;
    transform: translateY(20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;


const SectionContainer = styled.section`
  padding: 0px 16px;
  margin-top: -40px;

  @media ${MediaQuery.tablet} {
    padding: 0px 5rem;
    margin-top: -20px;
  }
  @media (min-width: 1024px) and (max-width: 1440px) {
    padding: 0px 5rem;
    margin-top: 0;
  }
  @media ${MediaQuery.laptopL} {
    padding: 0px 7rem;
    margin-top: 0;
  }
`;

const MainBlocks = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: 0;
  padding: 10px 0;

  @media ${MediaQuery.tablet} {
    padding: 50px 0;
  }
  @media ${MediaQuery.laptopL} {
    padding: 60px 0;
  }
`;

const TopRow = styled.div`
  display: flex;
  flex-direction: column;
  gap: 32px;
  width: 100%;
  align-items: flex-start;

  @media ${MediaQuery.laptopS} {
    flex-direction: row;
    gap: 5%;
    align-items: center;
  }
`;

const Left = styled.div`
  width: 100%;
  min-width: 0;

  @media ${MediaQuery.laptopS} {
    width: 45%;
  }
  @media ${MediaQuery.desktopS} {
    width: 45%;
  }
`;

const Heading = styled.h2<{ isVisible: boolean }>`
  font-family: 'Poppins-Regular';
  font-weight: 400;
  font-size: 32px;
  line-height: 1.24;
  margin-bottom: 25px;
  color: #000;
  letter-spacing: 0;
  opacity: 1;
  transform: translateY(0);
  transition: opacity 0.6s ease-out, transform 0.6s ease-out;

  @media ${MediaQuery.laptopS} {
    opacity: ${props => props.isVisible ? 1 : 0};
    transform: ${props => props.isVisible ? 'translateY(0)' : 'translateY(20px)'};
  }

  @media ${MediaQuery.mobileSMax} {
    font-family: 'Poppins-Regular';
    font-size: 32px;
  }
  @media ${MediaQuery.mobileL} {
    font-size: 32px;
  }
  @media ${MediaQuery.tablet} {
    font-size: 48px;
    line-height: 1.24;
    letter-spacing: 0;
  }
  @media ${MediaQuery.laptopS} {
    font-size: 68px;
    margin-bottom: 50px;
  }
  @media ${MediaQuery.desktopS} {
    font-size: 68px;
    margin-bottom: 60px;
  }
`;

const Subheading = styled.h4<{ isVisible: boolean }>`
  font-family: 'Poppins-Medium';
  font-weight: 500;
  font-size: 16px;
  line-height: 1.5;
  margin-bottom: 25px;
  color: #000;
  opacity: 1;
  transform: translateY(0);
  transition: opacity 0.6s ease-out, transform 0.6s ease-out;

  @media ${MediaQuery.laptopS} {
    opacity: ${props => props.isVisible ? 1 : 0};
    transform: ${props => props.isVisible ? 'translateY(0)' : 'translateY(20px)'};
  }

  @media ${MediaQuery.mobileSMax} {
    font-family: 'Poppins-Medium';
    font-size: 16px;
  }
  @media ${MediaQuery.mobileL} {
    font-size: 16px;
  }
  @media ${MediaQuery.tablet} {
    font-size: 18px;
    line-height: 1.24;
    letter-spacing: 0;
  }
  @media ${MediaQuery.laptopS} {
    font-size: 24px;
    margin-bottom: 45px;
  }
  @media ${MediaQuery.desktopS} {
    font-size: 24px;
    margin-bottom: 50px;
  }
`;

const Description = styled.p<{ isVisible: boolean }>`
  font-family: 'Poppins-Light';
  font-weight: 300;
  font-size: 22px;
  line-height: 1.8;
  margin-bottom: 0;
  color: #000;
  opacity: 0.8;
  transform: translateY(0);
  transition: opacity 0.6s ease-out, transform 0.6s ease-out;

  @media ${MediaQuery.laptopS} {
    opacity: ${props => props.isVisible ? 0.8 : 0};
    transform: ${props => props.isVisible ? 'translateY(0)' : 'translateY(20px)'};
  }

  @media ${MediaQuery.mobileSMax} {
    font-family: 'Poppins-Light';
    font-size: 22px;
  }
  @media ${MediaQuery.mobileL} {
    font-size: 22px;
  }
  @media ${MediaQuery.tablet} {
    font-size: 22px;
    line-height: 1.8;
    letter-spacing: 0;
  }
  @media ${MediaQuery.laptopS} {
    font-size: 22px;
    line-height: 1.8;
  }
  @media ${MediaQuery.desktopS} {
    font-size: 22px;
    line-height: 1.8;
  }
`;

const Stats = styled.div`
  display: flex;
  flex-direction: column;
  gap: 24px;
  width: 100%;
  align-items: stretch;

  @media ${MediaQuery.tablet} {
    width: 100%;
    gap: 32px;
  }
  @media ${MediaQuery.laptopS} {
    width: 50%;
    gap: 40px;
  }
  @media ${MediaQuery.desktopS} {
    width: 50%;
    gap: 48px;
  }
`;

const StatCard = styled.div`
  flex: 1 1 0;
  min-width: 0;
  background: #F8F8F8;
  border-radius: 30px;
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  align-items: flex-start;
  padding: 24px 32px;
  box-sizing: border-box;

  @media ${MediaQuery.tablet} {
    padding: 28px 40px;
  }
  @media ${MediaQuery.laptopS} {
    padding: 32px 48px;
  }
`;

const StatNumber = styled.div<{ isVisible: boolean }>`
  font-family: 'Poppins-regular';
  font-weight: 400;
  font-size: 120px;
  line-height: 1.24;
  margin-bottom: 10px;
  color: #000;
  opacity: 1;
  transform: translateY(0);
  transition: opacity 0.6s ease-out, transform 0.6s ease-out;

  @media ${MediaQuery.laptopS} {
    opacity: ${props => props.isVisible ? 1 : 0};
    transform: ${props => props.isVisible ? 'translateY(0)' : 'translateY(20px)'};
  }
`;

const StatLabel = styled.div<{ isVisible: boolean }>`
  font-family: 'Poppins-Medium';
  font-weight: 500;
  font-size: 24px;
  line-height: 1.24;
  margin-bottom: 8px;
  color: #000;
  opacity: 1;
  transform: translateY(0);
  transition: opacity 0.6s ease-out, transform 0.6s ease-out;

  @media ${MediaQuery.laptopS} {
    opacity: ${props => props.isVisible ? 1 : 0};
    transform: ${props => props.isVisible ? 'translateY(0)' : 'translateY(20px)'};
  }

  @media ${MediaQuery.mobileSMax} {
    font-family: 'Poppins-Medium';
    font-size: 24px;
  }
  @media ${MediaQuery.tablet} {
    font-size: 32px;
    line-height: 1.24;
    letter-spacing: 0;
  }
  @media ${MediaQuery.laptopS} {
    font-size: 48px;
  }
`;

const StatLabelSecond = styled.div<{ isVisible: boolean }>`
  font-family: 'Poppins-Medium';
  font-weight: 500;
  font-size: 18px;
  line-height: 1.24;
  margin-bottom: 8px;
  color: #000;
  opacity: 1;
  transform: translateY(0);
  transition: opacity 0.6s ease-out, transform 0.6s ease-out;

  @media ${MediaQuery.laptopS} {
    opacity: ${props => props.isVisible ? 1 : 0};
    transform: ${props => props.isVisible ? 'translateY(0)' : 'translateY(20px)'};
  }

  @media ${MediaQuery.mobileSMax} {
    font-family: 'Poppins-Medium';
    font-size: 18px;
  }
  @media ${MediaQuery.tablet} {
    font-size: 24px;
    line-height: 1.24;
    letter-spacing: 0;
  }
  @media ${MediaQuery.laptopS} {
    font-size: 32px;
  }
`;

const StatDesc = styled.div<{ isVisible: boolean }>`
  font-family: 'Poppins-Light';
  font-weight: 300;
  font-size: 16px;
  line-height: 1.5;
  color: #000;
  opacity: 0.8;
  transform: translateY(0);
  transition: opacity 0.6s ease-out, transform 0.6s ease-out;

  @media ${MediaQuery.laptopS} {
    opacity: ${props => props.isVisible ? 0.8 : 0};
    transform: ${props => props.isVisible ? 'translateY(0)' : 'translateY(20px)'};
  }

  @media ${MediaQuery.mobileSMax} {
    font-family: 'Poppins-Light';
    font-size: 16px;
  }
  @media ${MediaQuery.tablet} {
    font-size: 16px;
    line-height: 1.8;
    letter-spacing: 0;
  }
  @media ${MediaQuery.laptopS} {
    font-size: 16px;
  }
`;

const BottomRowBg = styled.div`
  width: 100vw;
  position: relative;
  left: 50%;
  right: 50%;
  margin-left: -50vw;
  margin-right: -50vw;
  background: #F8F8F8;
  margin-top: 0;
  border-radius: 0;
`;

const BottomRowInner = styled.div`
  display: flex;
  flex-direction: column;
  gap: 32px;
  width: 100%;
  align-items: flex-start;
  padding: 50px 16px;
  box-sizing: border-box;

  @media ${MediaQuery.mobileL} {
    padding: 60px 16px;
    gap: 40px;
  }
  @media ${MediaQuery.tablet} {
    padding: 70px 5rem;
    gap: 48px;
  }
  @media (min-width: 1024px) and (max-width: 1440px) {
    padding: 80px 5rem;
    gap: 50px;
  }
  @media (min-width: 1441px) {
    flex-direction: row;
    align-items: center;
    gap: 60px;
    padding: 80px 5rem;
  }
  @media ${MediaQuery.laptopL} {
    flex-direction: row;
    align-items: center;
    gap: 80px;
    padding: 100px 7rem;
  }
`;

const FundingImageStyled = styled.div`
  width: 100%;
  height: calc(200px + 10vw);
  background: #191C1D;
  border-radius: 30px;
  position: relative;
  overflow: hidden;
  display: flex;
  align-items: center;
  justify-content: center;

  img {
    width: 100%;
    height: 100%;
    object-fit: contain;
    border-radius: 30px;
    transition: all 0.3s ease;
  }

  .mobile-image {
    display: block;
  }
  .tablet-image {
    display: none;
  }
  .desktop-image {
    display: none;
  }

  @media ${MediaQuery.mobileL} {
    height: calc(250px + 8vw);
    
    img {
      width: 85%;
      height: 85%;
    }
  }
  @media ${MediaQuery.tablet} {
    width: 100%;
    height: calc(300px + 5vw);
    .mobile-image {
      display: none;
    }
    .tablet-image {
      display: block;
    }
    
    img {
      width: 90%;
      height: 90%;
    }
  }
  @media (min-width: 1024px) and (max-width: 1440px) {
    width: 100%;
    height: calc(320px + 4vw);
    .tablet-image {
      display: none;
    }
    .desktop-image {
      display: block;
    }
    
    img {
      width: 92%;
      height: 92%;
    }
  }
  @media (min-width: 1441px) {
    width: 45%;
    height: calc(350px + 3vw);
    .tablet-image {
      display: none;
    }
    .desktop-image {
      display: block;
    }
    
    img {
      width: 95%;
      height: 95%;
    }
  }
  @media ${MediaQuery.desktopS} {
    width: 45%;
    height: calc(400px + 2vw);
    .desktop-image {
      display: block;
    }
    
    img {
      width: 95%;
      height: 95%;
    }
  }
`;

const FundingText = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  justify-content: center;
  
  @media (min-width: 1441px) {
    width: 55%;
  }
  @media ${MediaQuery.desktopS} {
    width: 55%;
  }
`;

const FundingTextContainer = styled.div`
  width: 100%;
  
  @media ${MediaQuery.tablet} {
    padding-top: 0;
  }
  @media ${MediaQuery.laptopS} {
    padding-top: 0;
  }
  @media ${MediaQuery.desktopS} {
    padding-top: 0;
  }
`;

const FundingTitle = styled.h3<{ isVisible: boolean }>`
  font-family: 'Poppins-Regular';
  font-weight: 400;
  font-size: 32px;
  line-height: 1.24;
  margin-bottom: 25px;
  color: #000;
  letter-spacing: 0;
  opacity: 1;
  transform: translateY(0);
  transition: opacity 0.6s ease-out, transform 0.6s ease-out;

  @media ${MediaQuery.laptopS} {
    opacity: ${props => props.isVisible ? 1 : 0};
    transform: ${props => props.isVisible ? 'translateY(0)' : 'translateY(20px)'};
  }

  @media ${MediaQuery.mobileSMax} {
    font-family: 'Poppins-Regular';
    font-size: 32px;
  }
  @media ${MediaQuery.tablet} {
    font-size: 48px;
    line-height: 1.24;
    letter-spacing: 0;
  }
  @media ${MediaQuery.laptopS} {
    font-size: 68px;
  }
  @media ${MediaQuery.desktopS} {
    font-size: 68px;
  }
`;

const FundingDesc = styled.p<{ isVisible: boolean }>`
  font-family: 'Poppins-Light';
  font-weight: 300;
  font-size: 22px;
  line-height: 1.8;
  color: #000;
  margin-bottom: 25px;
  opacity: 0.8;
  transform: translateY(0);
  transition: opacity 0.6s ease-out, transform 0.6s ease-out;

  @media ${MediaQuery.laptopS} {
    opacity: ${props => props.isVisible ? 0.8 : 0};
    transform: ${props => props.isVisible ? 'translateY(0)' : 'translateY(20px)'};
  }

  @media ${MediaQuery.mobileSMax} {
    font-family: 'Poppins-Light';
    font-size: 22px;
  }
  @media ${MediaQuery.tablet} {
    font-size: 22px;
    line-height: 1.8;
    letter-spacing: 0;
  }
`;

const LearnMoreButton = styled.button`
  display: flex;
  justify-content: center;
  align-items: center;
  -webkit-tap-highlight-color: transparent;
  -webkit-touch-callout: none;
  -webkit-user-select: none;
  -khtml-user-select: none;
  -moz-user-select: none;
  -ms-user-select: none;
  user-select: none;
  outline: none;

  width: 12.5em;
  margin-top: 32px;

  border: 2px solid #000;
  background-color: #000;
  color: #fff;

  font-family: "Poppins-Light";
  font-size: 12px;

  cursor: pointer;
  transition: opacity 1s ease-in-out;
  border-radius: 50px;
  padding-top: 14px;
  padding-bottom: 14px;

  :hover {
    background-color: #fff;
    color: #000;
  }

  :active {
    background-color: #000;
    color: #fff;
  }

  @media ${MediaQuery.tablet} {
    font-family: "Poppins-Regular";
    font-size: 17px;
  }
`;

const MiddleRow = styled.div`
  width: 100vw;
  position: relative;
  left: 50%;
  right: 50%;
  margin-left: -50vw;
  margin-right: -50vw;
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 60px 16px;
  background: #282828;
  margin-top: 40px;

  @media ${MediaQuery.tablet} {
    padding: 70px 5rem;
    margin-top: 50px;
  }
  @media (min-width: 1024px) and (max-width: 1440px) {
    padding: 80px 5rem;
    margin-top: 55px;
  }
  @media ${MediaQuery.laptopL} {
    padding: 90px 7rem;
    margin-top: 60px;
  }
`;

const MiddleRowContent = styled.div`
  width: 100%;
  max-width: 1200px;
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;

  @media (min-width: 1400px) {
    max-width: 1400px;
  }
`;

const MiddleHeading = styled.h2<{ isVisible: boolean }>`
  font-family: 'Poppins-Regular';
  font-weight: 400;
  font-size: 32px;
  line-height: 1.4;
  text-align: center;
  margin-bottom: 60px;
  color: #fff;
  letter-spacing: 0;
  opacity: 1;
  transform: translateY(0);
  transition: opacity 0.6s ease-out, transform 0.6s ease-out;

  @media ${MediaQuery.mobileSMax} {
    font-size: 32px;
    line-height: 1.2;
    
    .mobile-break {
      display: block;
    }
    .desktop-break {
      display: none;
    }
  }
  
  .mobile-break {
    display: none;
  }
  .desktop-break {
    display: block;
  }

  @media ${MediaQuery.laptopS} {
    opacity: ${props => props.isVisible ? 1 : 0};
    transform: ${props => props.isVisible ? 'translateY(0)' : 'translateY(20px)'};
  }

  @media ${MediaQuery.mobileSMax} {
    font-family: 'Poppins-Regular';
  }
  @media ${MediaQuery.mobileL} {
    font-size: 32px;
  }
  @media ${MediaQuery.tablet} {
    line-height: 1.24;
    letter-spacing: 0;
    font-size: 48px;
  }
  @media ${MediaQuery.laptopS} {
    font-size: 68px;
  }
  @media ${MediaQuery.desktopS} {
    font-size: 68px;
  }
`;

const MiddleStatsRow = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 24px;
  width: 100%;
  margin-bottom: 16px;

  @media ${MediaQuery.tablet} {
    flex-direction: row;
    justify-content: center;
    gap: 40px;
  }
`;

const MiddleStatCard = styled.div`
  background: #F8F8F8;
  border-radius: 30px;
  padding: 40px 24px;
  min-width: 0;
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-family: 'Poppins-regular';
  font-size: 32px;
  font-weight: 400;
  line-height: 124%;
  color: #000;
  box-shadow: 0 2px 8px rgba(0,0,0,0.03);
  text-align: center;
  margin: 0 auto;

`;

const MiddleStatValue = styled.span`
  font-size: 75px;
  font-weight: 400;
  margin-right: 16px;
`;

const MiddleStatLabel = styled.span`
  font-size: 24px;
  font-weight: 400;
`;

const MiddleArrow = styled.div`
  width: 40px;
  height: 40px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #ABCDFF;
  border-radius: 50%;
  position: relative;

  .arrow-right {
    display: none;
  }
  .arrow-down {
    display: block;
  }

  @media ${MediaQuery.tablet} {
    .arrow-right {
      display: block;
    }
    .arrow-down {
      display: none;
    }
  }
`;

const MiddleCaption = styled.div<{ isVisible: boolean }>`
  font-family: 'Poppins-Medium';
  font-size: 18px;
  font-weight: 500;
  line-height: 2;
  color: #fff;
  text-align: center;
  opacity: 1;
  transform: translateY(0);
  transition: opacity 0.6s ease-out, transform 0.6s ease-out;

  @media ${MediaQuery.mobileSMax} {
    font-size: 18px;
    line-height: 1.6;
    
    .mobile-break {
      display: block;
    }
    .desktop-break {
      display: none;
    }
  }
  
  .mobile-break {
    display: none;
  }
  .desktop-break {
    display: block;
  }

  @media ${MediaQuery.laptopS} {
    opacity: ${props => props.isVisible ? 1 : 0};
    transform: ${props => props.isVisible ? 'translateY(0)' : 'translateY(20px)'};
  }

  @media ${MediaQuery.mobileSMax} {
    font-family: 'Poppins-Medium';
  }
  @media ${MediaQuery.tablet} {
    font-size: 24px;
    line-height: 1.5;
    letter-spacing: 0;
  }
  @media ${MediaQuery.laptopS} {
    font-size: 32px;
  }
`;

export const PatentSection: React.FC = () => 
{
    const sectionRef = useRef<HTMLDivElement>(null);
    const sectionEntry = useIntersectionObserver(sectionRef, { threshold: 0.2 });

    const handleLearnMoreClick = () => 
    {
        const el = document.querySelector('body');
        window?.scrollTo({
            left: 0,
            top: el?.scrollHeight,
            behavior: 'smooth',
        });
    };

    const isVisible = !!sectionEntry?.isIntersecting;

    return (
        <SectionContainer ref={sectionRef}>
            <MainBlocks>
                <TopRow>
                    <Left>
                        <Heading isVisible={isVisible}>Convert Your Software Into Strategic Patents</Heading>
                        <Subheading isVisible={isVisible}>Any technology can be reproduced— We can prevent that.</Subheading>
                        <Description isVisible={isVisible}>
                With a team of 40+ specialists in engineering, design, legal, and IP strategy, Speer helps forward-thinking companies build defensible, high-value IP portfolios. We'll ensure your technology is protected and commercially deployable.
                        </Description>
                    </Left>
                    <Stats>
                        <StatCard>
                            <StatNumber isVisible={isVisible}>35+</StatNumber>
                            <StatLabel isVisible={isVisible}>Patents Granted</StatLabel>
                            <StatDesc isVisible={isVisible}>
                  Officially recognized patents across cloud infrastructure, APIs, and modern software systems — built for scale and defensibility.
                            </StatDesc>
                        </StatCard>
                        <StatCard>
                            <StatNumber isVisible={isVisible}>10+</StatNumber>
                            <StatLabelSecond isVisible={isVisible}>Patents Pending</StatLabelSecond>
                            <StatDesc isVisible={isVisible}>
                  Patent applications filed to safeguard new hardware and system-level IP — supporting long-term defensibility.
                            </StatDesc>
                        </StatCard>
                    </Stats>
                </TopRow>
          
                <MiddleRow>
                    <MiddleRowContent>
                        <MiddleHeading isVisible={isVisible}>
                            <span className="mobile-break">We Turn Engineering<br />Costs Into Strategic<br />Returns</span>
                            <span className="desktop-break">We Turn Engineering Costs<br />Into Revenue Streams</span>
                        </MiddleHeading>
                        <MiddleCaption isVisible={isVisible}>
                            <span className="mobile-break">$1 in engineering spend = $1.75 in value<br />through SRED, grants and IP protection<br />licensing</span>
                            <span className="desktop-break">$1 in engineering spend = $1.75 in revenue within<br />the first year through our R&D network.</span>
                        </MiddleCaption>
                    </MiddleRowContent>
                </MiddleRow>
          
                <BottomRowBg>
                    <BottomRowInner>
                        <FundingImageStyled>
                            <img 
                                src={patentBottomImageMobile} 
                                alt="Funding Partners Mobile" 
                                className="mobile-image"
                            />
                            <img 
                                src={patentBottomImageTablet} 
                                alt="Funding Partners Tablet" 
                                className="tablet-image"
                            />
                            <img 
                                src={patentBottomImage} 
                                alt="Funding Partners Desktop" 
                                className="desktop-image"
                            />
                        </FundingImageStyled>
                        <FundingText>
                            <FundingTextContainer>
                                <FundingTitle isVisible={isVisible}>Fuel Your Innovation Through Our Network of Funding Partners</FundingTitle>
                                <FundingDesc isVisible={isVisible}>
                    Speer partners with delivery organizations and advisors to help you secure project funding — from federal and provincial subsidies to R&D grants.<br /><br />
                    We've raised over $6.5M USD in combined venture, government, and grant funding. Whether you're early-stage or expanding, we'll align your IP and delivery roadmap with the right funding sources.
                                </FundingDesc>
                                <LearnMoreButton onClick={handleLearnMoreClick}>Learn More</LearnMoreButton>
                            </FundingTextContainer>
                        </FundingText>
                    </BottomRowInner>
                </BottomRowBg>
            </MainBlocks>
        </SectionContainer>
    );
};